import React, { useEffect, useMemo, useState } from "react";
import "./AssessmentQnA.scss";

import Spinner from "../../atoms/Spinner/Spinner";
import { AssessmentId } from "../../services/rest/kairaAssessments";
import { useSharedProfile } from "../../organisms/PatientDetailsV2/SharedProfile/useSharedProfile";
import {
  formatVisitDate,
  sessionOccurrenceLabel,
} from "../../organisms/PatientDetailsV2/CoachAssessment/prevHistory";
import { formatAnswer } from "./formatAnswer";
import { QnASection, useAssessmentQnA } from "./useAssessmentQnA";

type View = "SHARED" | "BASELINE" | "FOLLOWUPS";

const VIEWS: { label: string; value: View }[] = [
  { label: "Shared Profile", value: "SHARED" },
  { label: "Baseline Assessment", value: "BASELINE" },
  { label: "Follow-ups", value: "FOLLOWUPS" },
];

const STATUS_LABEL: Record<string, string> = {
  published: "Published",
  preview: "Submitted",
  draft: "In progress",
};

const Message: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="qna-message">{children}</div>
);

const QnAList: React.FC<{
  sections: QnASection[];
  answers: Record<string, unknown>;
  scales?: any;
  showUnanswered: boolean;
  emptyText?: string;
}> = ({ sections, answers, scales, showUnanswered, emptyText }) => {
  const rendered = sections
    .map((section) => ({
      section,
      rows: section.questions
        .map((question) => ({
          question,
          answer: formatAnswer(
            question,
            answers[question.id],
            section.scales ?? scales,
          ),
        }))
        .filter((row) => showUnanswered || row.answer.kind !== "empty"),
    }))
    .filter(({ rows }) => rows.length > 0);

  if (!rendered.length) {
    return (
      <Message>
        {showUnanswered
          ? "No questions in this assessment."
          : emptyText || "No answers recorded yet."}
      </Message>
    );
  }

  return (
    <div className="qna-sections">
      {rendered.map(({ section, rows }) => (
        <section className="qna-section" key={section.id}>
          {section.name ? <h4 className="qna-section-title">{section.name}</h4> : null}
          <dl className="qna-list">
            {rows.map(({ question, answer }) => (
              <div className="qna-row" key={question.id}>
                <dt className="qna-question">{question.prompt || question.id}</dt>
                <dd className={`qna-answer is-${answer.kind}`}>
                  {answer.kind === "answered"
                    ? answer.lines.map((line, idx) => <div key={idx}>{line}</div>)
                    : answer.kind === "skipped"
                      ? "Skipped"
                      : "Not answered"}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
};

const SessionPanel: React.FC<{
  session: any;
  qna: ReturnType<typeof useAssessmentQnA>;
  showUnanswered: boolean;
}> = ({ session, qna, showUnanswered }) => {
  const { loadDetail, details, detailLoading, detailError, sessionKey } = qna;
  const id = sessionKey(session);
  const detail = details[id];
  const error = detailError[id];

  useEffect(() => {
    loadDetail(session);
  }, [loadDetail, session]);

  const status = STATUS_LABEL[session?.action || "draft"] || "In progress";
  const filledBy = session?.source === "coach" ? "Coach" : "Patient";

  return (
    <div className="qna-session">
      <div className="qna-session-meta">
        <span className="qna-session-label">{sessionOccurrenceLabel(session)}</span>
        {formatVisitDate(session) ? <span>{formatVisitDate(session)}</span> : null}
        <span className={`qna-status is-${session?.action || "draft"}`}>{status}</span>
        <span>Filled by {filledBy}</span>
      </div>
      {detail ? (
        <QnAList
          sections={detail.sections}
          answers={detail.answers}
          scales={detail.scales}
          showUnanswered={showUnanswered}
          emptyText="No answers recorded for this visit's own questions. Answers saved through the Shared Profile appear under the Shared Profile tab."
        />
      ) : error ? (
        <Message>{error}</Message>
      ) : detailLoading[id] !== false ? (
        // Also covers the first render, before loadDetail's effect has run.
        <div className="qna-loader">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Read-only question/answer view of the Kaira assessments for one coach type:
 * Shared Profile, Baseline and Follow-ups.
 */
const AssessmentQnA: React.FC<{
  assessmentId: AssessmentId;
  patientId: string | undefined;
}> = ({ assessmentId, patientId }) => {
  const [view, setView] = useState<View>("SHARED");
  const [showUnanswered, setShowUnanswered] = useState(false);
  const [followUpId, setFollowUpId] = useState<string | null>(null);

  const shared = useSharedProfile(patientId);
  const qna = useAssessmentQnA(assessmentId, patientId);
  const { baseline, followUps, sessionKey } = qna;

  const selectedFollowUp = useMemo(
    () =>
      followUps.find((s) => sessionKey(s) === followUpId) || followUps[0] || null,
    [followUpId, followUps, sessionKey],
  );

  // Diet and exercise configs can both carry a shared section/question; show each once.
  const sharedQnASections = useMemo(() => {
    const seenSections = new Set<string>();
    return shared.sharedSections
      .filter((section: any) => {
        if (seenSections.has(section.id)) return false;
        seenSections.add(section.id);
        return true;
      })
      .map((section: any) => {
        const seenQuestions = new Set<string>();
        return {
          id: String(section.id),
          name: section.name,
          scales: shared.scalesByAssessment[section.assessmentId as AssessmentId],
          questions: (shared.questionsBySection[section.id] || []).filter(
            (q: any) => !seenQuestions.has(q.id) && !!seenQuestions.add(q.id),
          ),
        };
      });
  }, [shared.questionsBySection, shared.scalesByAssessment, shared.sharedSections]);

  const renderView = () => {
    if (!patientId) return <Message>Patient not found.</Message>;

    if (view === "SHARED") {
      if (shared.loading) {
        return (
          <div className="qna-loader">
            <Spinner />
          </div>
        );
      }
      if (shared.error) return <Message>{shared.error}</Message>;
      return (
        <QnAList
          sections={sharedQnASections}
          answers={shared.answers}
          showUnanswered={showUnanswered}
        />
      );
    }

    if (qna.loading) {
      return (
        <div className="qna-loader">
          <Spinner />
        </div>
      );
    }
    if (qna.error) return <Message>{qna.error}</Message>;

    if (view === "BASELINE") {
      return baseline ? (
        <SessionPanel session={baseline} qna={qna} showUnanswered={showUnanswered} />
      ) : (
        <Message>No baseline assessment yet.</Message>
      );
    }

    if (!selectedFollowUp) return <Message>No follow-up assessments yet.</Message>;
    return (
      <>
        <div className="qna-followup-picker">
          {followUps.map((session) => (
            <button
              type="button"
              key={sessionKey(session)}
              aria-pressed={sessionKey(session) === sessionKey(selectedFollowUp)}
              className={`qna-chip${
                sessionKey(session) === sessionKey(selectedFollowUp)
                  ? " qna-chip--active"
                  : ""
              }`}
              onClick={() => setFollowUpId(sessionKey(session))}
            >
              {sessionOccurrenceLabel(session)}
              {formatVisitDate(session) ? (
                <span className="qna-chip-date">{formatVisitDate(session)}</span>
              ) : null}
            </button>
          ))}
        </div>
        <SessionPanel
          key={sessionKey(selectedFollowUp)}
          session={selectedFollowUp}
          qna={qna}
          showUnanswered={showUnanswered}
        />
      </>
    );
  };

  return (
    <div className="assessment-qna">
      <div className="qna-toolbar">
        <div className="qna-segments" role="tablist">
          {VIEWS.map((item) => (
            <button
              type="button"
              role="tab"
              aria-selected={view === item.value}
              key={item.value}
              className={`qna-segment${
                view === item.value ? " qna-segment--active" : ""
              }`}
              onClick={() => setView(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <label className="qna-toggle">
          <input
            type="checkbox"
            checked={showUnanswered}
            onChange={(e) => setShowUnanswered(e.target.checked)}
          />
          Show unanswered questions
        </label>
      </div>
      {renderView()}
    </div>
  );
};

export default AssessmentQnA;
